import React, { useMemo } from 'react';
import { User, Calendar, Star, Cake, HeartPulse, BookOpen, Trophy, Leaf } from 'lucide-react';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент для расчета возраста и связанной информации
 */
const AgeCalculator = ({ 
  formData, 
  setFormData, 
  results, 
  isCalculating 
}) => {
  
  // Знаки зодиака с датами
  const zodiacSigns = useMemo(() => [
    { sign: 'Овен', start: { month: 3, day: 21 }, end: { month: 4, day: 19 } },
    { sign: 'Телец', start: { month: 4, day: 20 }, end: { month: 5, day: 20 } },
    { sign: 'Близнецы', start: { month: 5, day: 21 }, end: { month: 6, day: 20 } },
    { sign: 'Рак', start: { month: 6, day: 21 }, end: { month: 7, day: 22 } },
    { sign: 'Лев', start: { month: 7, day: 23 }, end: { month: 8, day: 22 } },
    { sign: 'Дева', start: { month: 8, day: 23 }, end: { month: 9, day: 22 } },
    { sign: 'Весы', start: { month: 9, day: 23 }, end: { month: 10, day: 22 } },
    { sign: 'Скорпион', start: { month: 10, day: 23 }, end: { month: 11, day: 21 } },
    { sign: 'Стрелец', start: { month: 11, day: 22 }, end: { month: 12, day: 21 } },
    { sign: 'Козерог', start: { month: 12, day: 22 }, end: { month: 1, day: 19 } },
    { sign: 'Водолей', start: { month: 1, day: 20 }, end: { month: 2, day: 18 } },
    { sign: 'Рыбы', start: { month: 2, day: 19 }, end: { month: 3, day: 20 } }
  ], []);
  
  // Китайский зодиак
  const chineseZodiac = useMemo(() => [
    'Крыса', 'Бык', 'Тигр', 'Кролик', 'Дракон', 'Змея', 
    'Лошадь', 'Коза', 'Обезьяна', 'Петух', 'Собака', 'Свинья'
  ], []);
  
  // Жизненные этапы по возрасту в годах
  const lifeStages = useMemo(() => [
    { name: 'Младенец', min: 0, max: 1, icon: 'baby' },
    { name: 'Ранний детский возраст', min: 1, max: 3, icon: 'baby' },
    { name: 'Дошкольный возраст', min: 3, max: 7, icon: 'baby' },
    { name: 'Младший школьный возраст', min: 7, max: 12, icon: 'child' },
    { name: 'Подростковый возраст', min: 12, max: 18, icon: 'child' },
    { name: 'Юность', min: 18, max: 21, icon: 'youth' },
    { name: 'Молодость', min: 21, max: 35, icon: 'adult' },
    { name: 'Средний возраст', min: 35, max: 60, icon: 'adult' },
    { name: 'Пожилой возраст', min: 60, max: 75, icon: 'elderly' },
    { name: 'Старческий возраст', min: 75, max: 90, icon: 'elderly' },
    { name: 'Долгожитель', min: 90, max: 200, icon: 'elderly' }
  ], []);
  
  // Интересные факты о возрасте
  const ageFacts = useMemo(() => {
    if (!results) return [];
    
    return [
      { 
        title: 'Сердцебиения', 
        value: Math.round(results.totalDays * 24 * 60 * 70), 
        description: 'Примерное количество сокращений сердца за вашу жизнь (при средней частоте 70 ударов в минуту)',
        icon: HeartPulse
      },
      { 
        title: 'Пройденное расстояние', 
        value: `${Math.round(results.totalDays * 5 / 1000)} км`, 
        description: 'Примерное расстояние, которое вы прошли за всю жизнь (при средней активности 5 км в день)',
        icon: Leaf
      },
      { 
        title: 'Время сна', 
        value: `${Math.round(results.totalDays * 8 / 24 / 365.25 * 10) / 10} лет`, 
        description: 'Время, проведенное во сне (при средней продолжительности сна 8 часов в день)',
        icon: BookOpen
      },
      { 
        title: 'Орбитальные путешествия', 
        value: Math.floor(results.years), 
        description: 'Количество полных оборотов вместе с Землей вокруг Солнца',
        icon: Trophy
      }
    ];
  }, [results]);
  
  // Функция для получения текущего этапа жизни
  const getCurrentLifeStage = (years) => {
    return lifeStages.find(stage => years >= stage.min && years < stage.max) || lifeStages[lifeStages.length - 1];
  };
  
  // Форматирование даты для отображения
  const formatDisplayDate = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      weekday: 'long'
    });
  };
  
  // Склонение слов в зависимости от числа
  const pluralize = (number, one, few, many) => {
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return many;
    if (lastDigit === 1) return one;
    if (lastDigit >= 2 && lastDigit <= 4) return few;
    return many;
  };
  
  if (isCalculating || !formData.birthdayDate || !results) {
    return null;
  }
  
  // Текущий этап жизни
  const currentStage = getCurrentLifeStage(results.years);
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <User className="w-5 h-5 mr-2 text-purple-500" />
        Расчет возраста
      </h3>
      
      {/* Основная информация */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
          <div className="flex items-start">
            <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-800 p-2 rounded-full">
              <User className="w-6 h-6 text-purple-600 dark:text-purple-300" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
                Дата рождения
              </h4>
              <p className="text-lg font-bold text-purple-800 dark:text-purple-200">
                {formatDisplayDate(results.birthdayDate)}
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                День недели: {new Date(results.birthdayDate).toLocaleDateString('ru-RU', { weekday: 'long' })}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-violet-50 dark:bg-violet-900/20 p-4 rounded-lg border border-violet-100 dark:border-violet-800">
          <div className="flex items-start">
            <div className="flex-shrink-0 bg-violet-100 dark:bg-violet-800 p-2 rounded-full">
              <Calendar className="w-6 h-6 text-violet-600 dark:text-violet-300" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-violet-700 dark:text-violet-300 mb-1">
                Дата для расчета
              </h4>
              <p className="text-lg font-bold text-violet-800 dark:text-violet-200">
                {formatDisplayDate(results.referenceDate)}
              </p>
              <p className="text-xs text-violet-600 dark:text-violet-400 mt-1">
                {formatDisplayDate(results.referenceDate) === formatDisplayDate(new Date()) 
                  ? 'Сегодняшняя дата' 
                  : 'Пользовательская дата'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Текущий возраст */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30 p-5 rounded-lg border border-violet-100 dark:border-violet-800 mb-6">
        <h4 className="text-base font-medium text-violet-800 dark:text-violet-200 mb-3 flex items-center">
          <Cake className="w-5 h-5 mr-2 text-violet-600 dark:text-violet-400" />
          Текущий возраст
        </h4>
        
        <div className="text-3xl font-bold text-violet-900 dark:text-violet-100 mb-2">
          {results.formattedAge}
        </div>
        
        <div className="text-sm text-violet-700 dark:text-violet-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <span className="font-medium">Полных лет:</span> {results.years}
            </div>
            <div>
              <span className="font-medium">Месяцев сверх полных лет:</span> {results.months}
            </div>
            <div>
              <span className="font-medium">Дней сверх месяцев:</span> {results.days}
            </div>
            <div>
              <span className="font-medium">Возрастная категория:</span> {currentStage.name}
            </div>
          </div>
        </div>
      </div>
      
      {/* Альтернативное представление возраста */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            В днях
          </h4>
          <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
            {results.totalDays.toLocaleString('ru-RU')} {pluralize(results.totalDays, 'день', 'дня', 'дней')}
          </p>
        </div>
        
        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-100 dark:border-teal-800">
          <h4 className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            В неделях
          </h4>
          <p className="text-xl font-bold text-teal-800 dark:text-teal-200">
            {results.totalWeeks.toLocaleString('ru-RU')} {pluralize(results.totalWeeks, 'неделя', 'недели', 'недель')}
          </p>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            В месяцах
          </h4>
          <p className="text-xl font-bold text-green-800 dark:text-green-200">
            {results.totalMonths.toLocaleString('ru-RU')} {pluralize(results.totalMonths, 'месяц', 'месяца', 'месяцев')}
          </p>
        </div>
      </div>
      
      {/* Знаки зодиака */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-100 dark:border-amber-800">
          <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1 flex items-center">
            <Star className="w-4 h-4 mr-1" />
            Знак зодиака
          </h4>
          <p className="text-xl font-bold text-amber-800 dark:text-amber-200">
            {results.zodiacSign}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Западный (тропический) зодиак
          </p>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-100 dark:border-red-800">
          <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-1 flex items-center">
            <Star className="w-4 h-4 mr-1" />
            Китайский знак
          </h4>
          <p className="text-xl font-bold text-red-800 dark:text-red-200">
            {results.chineseSign}
          </p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            Восточный календарь
          </p>
        </div>
      </div>
      
      {/* Следующий день рождения */}
      <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg border border-pink-100 dark:border-pink-800 mb-6">
        <h4 className="text-sm font-medium text-pink-700 dark:text-pink-300 mb-1 flex items-center">
          <Cake className="w-4 h-4 mr-1" />
          Следующий день рождения
        </h4>
        <p className="text-xl font-bold text-pink-800 dark:text-pink-200">
          {formatDisplayDate(results.nextBirthday)}
        </p>
        <p className="text-sm text-pink-600 dark:text-pink-400 mt-1">
          Осталось {results.daysToBirthday} {pluralize(results.daysToBirthday, 'день', 'дня', 'дней')}
        </p>
      </div>
      
      {/* Интересные факты */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Интересные факты о вашей жизни
          </h4>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {ageFacts.map((fact, index) => (
            <div key={index} className="p-4 flex">
              <div className="flex-shrink-0 mt-1">
                <fact.icon className="w-5 h-5 text-indigo-500" />
              </div>
              <div className="ml-3">
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {fact.title}: <span className="font-bold">{fact.value.toLocaleString('ru-RU')}</span>
                </h5>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {fact.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgeCalculator;